//
//  redux/authentication/authenticationActions.tsx
//  Core
//
//  Dante Mei on 2024/10/16.
//  Copyright © 2024 arimaslab srl
//  All rights reserved.
//
import axios from 'axios'
import {createAsyncThunk, PayloadAction, createAction, Action} from '@reduxjs/toolkit'
import { sliceStateStatus, systemStatus } from '../../../../Core';
import {routesIndex} from '@/RoutesIndex';

import IAuthentication from "@/interface/core/IAuthentication";
import ISliceState from '@/interface/core/ISliceState';
import { generateAuthStateDataItem } from '../../../hooks';

export const login = createAsyncThunk(
    'authentication/login',
    async (_data:ISliceState["data"]["login"], { rejectWithValue }) => {
      try {

        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        }

        let url = routesIndex.api.authentication.csrf;
        await axios.get(url);

        url = routesIndex.api.prefix + routesIndex.api.authentication.login;
        const q = axios.post(
          url,
          _data,
          config
        );

        const r = (await q).data;
        return r;

      }
      catch (error: any) {
        if (error.response && error.response.data.message) {
          return rejectWithValue(error.response.data.message)
        }
        else {
          return rejectWithValue(error.message)
        }
      }
    }
)

export const me = createAsyncThunk(
  'authentication/me',
  async (_, { rejectWithValue }) => {
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }

      let url = routesIndex.api.prefix + routesIndex.api.authentication.user;
      const q = axios.get(
        url,
        config
      );
      
      const r = (await q).data;
      if(r.result.status == systemStatus.active) {
        return setStateData(r);
      }
      else {
        return rejectWithValue('messages.auth.accessDenied');
      }
    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }
  }
)

export const forgotPassword = createAsyncThunk(
  'authentication/forgotpassword',
  async (_data:ISliceState["data"]["login"], { rejectWithValue }) => {
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }

      let url = routesIndex.api.authentication.csrf;
      await axios.get(url);

      url = routesIndex.api.prefix + routesIndex.api.authentication.forgotpassword;
      const q = axios.post(
        url, 
        _data, 
        config
      );

      const r = (await q).data;
      return r;
    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }
  }
)

export const resetPassword = createAsyncThunk(
  'authentication/resetpassword',
  async (_data:ISliceState["data"], { rejectWithValue }) => {
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }

      let url = routesIndex.api.authentication.csrf;
      await axios.get(url);

      url = routesIndex.api.prefix + routesIndex.api.authentication.resetpassword;
      const q = axios.post(
        url, 
        _data, 
        config
      );

      const r = (await q).data;
      return r;

    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }
  }
)

export const twoFactorAuthentication = createAsyncThunk(
  'authentication/twofactorauthentication',
  async (_data: boolean, { rejectWithValue }) => {
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }

      let url = routesIndex.api.prefix + routesIndex.api.authentication.twofactorauthentication;
      let q;
      
      if(_data) {
        q = axios.post(
          url,
          [],
          config
        );
      }
      else {
        q = axios.delete(
          url,
          config
        );
      }

      const r = (await q).data;
      return r;

    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }
  }
)

export const twoFactorChallenge = createAsyncThunk(
  'authentication/twofactorchallenge',
  async (_data: string, { rejectWithValue }) => {
    
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }


      let url = routesIndex.api.prefix + routesIndex.api.authentication.twofactorchallenge;
      let q = axios.post(
        url,
        {
          code: _data,
          recovery_code: ''
        },
        config
      );

      const r = (await q).data;
      return r;

    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }

  }
)

export const twoFactorQRCode = createAsyncThunk(
  'authentication/twofactorqrcode',
  async (_, { rejectWithValue }) => {
    
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }


      let url = routesIndex.api.prefix + routesIndex.api.authentication.twofactorqrcode;
      let q = axios.get(
        url,
        config
      );

      const r = (await q).data;
      return r;

    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }

  }
)

export const registration = createAsyncThunk(
  'authentication/registration',
  async (_data:any, { rejectWithValue }) => {
    try {

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      }

      //let url = routesIndex.api.authentication.csrf;
      //await axios.get(url);

      let url = routesIndex.api.prefix + routesIndex.api.authentication.registration;
      const q = axios.post(
        url,
        _data,
        config
      );

      const r = (await q).data;
      return r;

    }
    catch (error: any) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message)
      }
      else {
        return rejectWithValue(error.message)
      }
    }
  }
)

export const setAuthData = createAction<IAuthentication>('setAuthData')
export const resetAuthData = createAction<IAuthentication>('resetAuthData')

export const logout = createAction('logout')
export const resetAuthentication = createAction('resetAuthentication')


const setStateData = (apiResponse: any) => {

  let stateData: ISliceState = {
    data: {} as IAuthentication,
    status: sliceStateStatus.fulfilled
  };

  if(apiResponse.status && apiResponse.result) {
    Object.assign(stateData.data, generateAuthStateDataItem(apiResponse.result));
  }
  
  return stateData;
};
